/**
 * THE CONTENT TREE — the things you are working in, and how they are arranged.
 *
 * A leaf is one SESSION or one DOCUMENT. Not "the agent panel" or "the canvas":
 * an instance, with its own id and its own subject, so two sessions can sit side
 * by side and a document can sit beside the conversation about it. That is the
 * whole reason this is a tree rather than a set of named slots — nesting and
 * REPETITION both mean something here.
 *
 * WHAT IS NOT IN IT. The files sidebar and settings are chrome: you never want
 * two of them and you never split one in half, so they are a flag and a width in
 * `useLayout`, not nodes. Keeping them out is what removes the awkward parts of
 * the previous shape — inserting settings into the tree on first open, hiding a
 * node without unmounting it, and the rule that each panel kind appears once,
 * which was exactly what prevented two sessions.
 *
 * FOUR RULES, and every operation preserves them. They are stated because a tree
 * that breaks one renders as a blank or a zero-width pane, which is very hard to
 * diagnose from the screen:
 *
 *   1. Leaf ids are UNIQUE. Two leaves may show the same session — that is a
 *      legitimate thing to want — but they are still two leaves.
 *   2. A split has >= 2 children. One child is not a split, it is that child.
 *   3. A split's children never repeat its direction: row[a, row[b, c]] and
 *      row[a, b, c] look identical and behave differently, so only the flat form
 *      is representable.
 *   4. `sizes` has one fraction per child, each >= MIN_FRACTION, summing to 1.
 *
 * `normalize` enforces 2-4 and every mutation ends with it. Rule 1 is enforced on
 * the way in, by minting ids here rather than accepting them from callers.
 */

import { isCodeId } from '../../services/code/codeFile'

/**
 * What a block shows.
 *
 * An AGENT block is one kind of thing, whose state is which session it is on —
 * or none, meaning the session list. That keeps the list↔thread move a property
 * of the block (the back button goes back), while two conversations at once is
 * two blocks.
 *
 * A DOCUMENT block shows one document or app.
 *
 * EVERY BLOCK NAMES ITS PROJECT (RFC 0281). A document id is global, so a leaf
 * was already addressable across projects; what is not global is everything
 * needed to RENDER one, because the documents, apps and comments registries all
 * live on the project document. Finding which project holds an id by scanning
 * every project is not a lookup to do per render, so the project rides along as
 * a pointer. The document id still identifies, the project id LOCATES.
 *
 * An agent block on `projectId: null` is the session list across EVERY project,
 * which is the inbox that having more than one immediately demands. On a
 * project, it is that project's list.
 */
export type AgentContent =
  /** The inbox: every project's sessions, which is what a fresh window opens on. */
  | { kind: 'agent'; projectId: null; sessionId: null }
  /** One project: its list, or one conversation in it. */
  | { kind: 'agent'; projectId: string; sessionId: string | null }

/**
 * A pane that has not been told what it is for yet: the new-tab page.
 *
 * What ⌘D opens. A split has to put SOMETHING in the new half, and the session
 * list was the wrong something — it answers "what goes here" with sessions
 * only, when the honest answer is "anything this project has". So the empty
 * pane is its own kind of content, showing what you have been in recently, and
 * picking from it turns this pane into that thing.
 *
 * A kind rather than a null: the pane is real, it is addressable, and it comes
 * back on reload like every other pane. `projectId` is the project its list is
 * drawn from, null being the same "across everything" it means for an agent
 * block.
 */
export type PickerContent = { kind: 'picker'; projectId: string | null }

/** Discover is a World page. A lineage selects one package in the same pane. */
export type DiscoverContent = { kind: 'discover'; lineageId: string | null }

/**
 * A shell on a machine (RFC 0276).
 *
 * MODELLED ON THE AGENT BLOCK, NOT ON THE DOCUMENT. `terminalId: null` is that
 * device's LIST of shells, the way `sessionId: null` is the session list, so a
 * second shell costs a selection rather than a pane and the back button goes
 * back. That is also the answer to wanting several terminals without spending
 * several panes on them.
 *
 * Smuggling a terminal in as a document (a self-describing `term:…` id, the way
 * a web page is smuggled in) would have cost no layout code at all, and it
 * inherits exactly the wrong rule: documents replace each other in a pane. A
 * session survives being closed because it is on the log; a pty does not,
 * because closing one ends the process. So opening a file would kill a running
 * build, and opening a shell would evict the file you were reading.
 *
 * NO PROJECT, deliberately. Every other leaf names one because what it needs to
 * render is catalogued on a project document. A shell is catalogued on a
 * MACHINE, and the device id is what locates it — which is why the device rides
 * in the leaf for the block's whole life rather than being parsed back out of
 * an id string.
 */
export type TerminalContent = {
  kind: 'terminal'
  deviceId: string
  terminalId: string | null
  /** Whose checkout the shell is in (RFC 0331 3a). Absent on a pane made
   *  before it was recorded, or restored from a url; the window's active
   *  project stands in. */
  projectId?: string
}

/**
 * A document block. `docId` is what it shows.
 *
 * A CODE PANE HOLDS TABS. Opening a project file (`code:` id) while a code
 * pane is in the space adds it to that pane rather than replacing what it
 * shows, so the files of a review sit side by side in one header instead of
 * evicting each other. `tabs` is every file the pane holds, in order, and
 * always includes `docId`. Absent while there is only one: a pane with one
 * file is exactly the document block it always was, in the tree and the url.
 */
export type DocumentContent = {
  kind: 'document'
  projectId: string
  docId: string
  tabs?: string[]
}

export type Content =
  | AgentContent
  | PickerContent
  | DiscoverContent
  | TerminalContent
  | DocumentContent

export interface ContentLeaf {
  type: 'leaf'
  /** Unique within the tree, and stable across rearrangement. */
  id: string
  content: Content
}

export type Direction = 'row' | 'column'

export interface ContentSplit {
  type: 'split'
  direction: Direction
  children: ContentNode[]
  /** One fraction per child, each >= MIN_FRACTION, summing to 1. */
  sizes: number[]
}

export type ContentNode = ContentLeaf | ContentSplit

/** Child indices from the root. `[]` is the root itself. */
export type NodePath = number[]

/** Where a dragged pane lands, relative to the leaf it was dropped on. */
export type DropEdge = 'left' | 'right' | 'top' | 'bottom' | 'center'

/**
 * The smallest share a pane can be squeezed to. A fraction, not pixels, because
 * the tree knows nothing about the window. 8% keeps a squeezed pane grabbable
 * rather than letting a drag erase it.
 */
export const MIN_FRACTION = 0.08

let seq = 0
/** Mint a leaf id. Ids are opaque and never parsed — they exist to be unique. */
export function leafId(): string {
  seq += 1
  return `c${seq}`
}

export const leaf = (content: Content, id = leafId()): ContentLeaf => ({
  type: 'leaf',
  id,
  content,
})

/**
 * An agent block. No session means the list, which is where a new one starts;
 * no project means that list spans every project in the World.
 */
export function agentLeaf(): ContentLeaf
export function agentLeaf(
  projectId: string,
  sessionId?: string | null,
): ContentLeaf
export function agentLeaf(
  projectId: string | null = null,
  sessionId: string | null = null,
): ContentLeaf {
  return leaf(
    projectId === null
      ? { kind: 'agent', projectId: null, sessionId: null }
      : { kind: 'agent', projectId, sessionId },
  )
}

export const documentLeaf = (projectId: string, docId: string): ContentLeaf =>
  leaf({ kind: 'document', projectId, docId })

/**
 * A terminal block. No shell means that machine's list, which is where a new
 * one starts.
 */
export const terminalLeaf = (
  deviceId: string,
  terminalId: string | null = null,
): ContentLeaf => leaf({ kind: 'terminal', deviceId, terminalId })

/** An empty pane, waiting to be told what it is for. */
export const pickerLeaf = (projectId: string | null = null): ContentLeaf =>
  leaf({ kind: 'picker', projectId })

const equalSizes = (count: number): number[] =>
  count > 0 ? Array.from({ length: count }, () => 1 / count) : []

export function split(
  direction: Direction,
  children: ContentNode[],
): ContentSplit {
  return {
    type: 'split',
    direction,
    children,
    sizes: equalSizes(children.length),
  }
}

/**
 * Clamp fractions to MIN_FRACTION and renormalize to sum 1.
 *
 * Clamping alone breaks the sum and normalizing alone lets a pane reach zero, so
 * both happen together: clamp, then take the excess from the panes that have room
 * in proportion to their headroom. When even the minimums cannot fit, equal shares
 * are the only honest answer.
 */
export function normalizeSizes(sizes: number[], count: number): number[] {
  if (count <= 0) return []
  if (count * MIN_FRACTION > 1) return equalSizes(count)
  const raw = Array.from({ length: count }, (_, i) => {
    const v = sizes[i]
    return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : 0
  })
  const total = raw.reduce((a, b) => a + b, 0)
  const scaled = total > 0 ? raw.map((v) => v / total) : equalSizes(count)
  const clamped = scaled.map((v) => Math.max(v, MIN_FRACTION))
  const excess = clamped.reduce((a, b) => a + b, 0) - 1
  if (Math.abs(excess) < 1e-9) return clamped
  const headroom = clamped.map((v) => v - MIN_FRACTION)
  const totalHeadroom = headroom.reduce((a, b) => a + b, 0)
  if (totalHeadroom <= 0) return equalSizes(count)
  return clamped.map((v, i) => v - (excess * headroom[i]) / totalHeadroom)
}

/** Enforce rules 2-4 bottom-up. Null when nothing is left to render. */
export function normalize(node: ContentNode | null): ContentNode | null {
  if (!node) return null
  if (node.type === 'leaf') return node

  const children: ContentNode[] = []
  const sizes: number[] = []
  node.children.forEach((child, i) => {
    const n = normalize(child)
    if (!n) return
    const share = node.sizes[i] ?? 1 / node.children.length
    // Rule 3: a same-direction child merges in, its shares scaled into the slice
    // it occupied.
    if (n.type === 'split' && n.direction === node.direction) {
      n.children.forEach((grandchild, j) => {
        children.push(grandchild)
        sizes.push(share * (n.sizes[j] ?? 1 / n.children.length))
      })
      return
    }
    children.push(n)
    sizes.push(share)
  })

  if (children.length === 0) return null
  // Rule 2: a split of one is that one.
  if (children.length === 1) return children[0]
  return {
    type: 'split',
    direction: node.direction,
    children,
    sizes: normalizeSizes(sizes, children.length),
  }
}

/** Every leaf, in visual order (left to right, top to bottom). */
export function leaves(node: ContentNode | null): ContentLeaf[] {
  if (!node) return []
  if (node.type === 'leaf') return [node]
  return node.children.flatMap(leaves)
}

/**
 * The LEAF with this id, or null.
 *
 * Sits beside `findLeaf`, which answers a different question: `findLeaf` gives
 * the PATH, because splitting and resizing need the parent, while this gives
 * the pane itself, because everything that asks what a pane is SHOWING needs
 * its content. Two functions rather than one returning a pair every caller
 * would half unpack — and named so that reaching for `.content` on the wrong
 * one is hard to do.
 */
export function leafAt(
  node: ContentNode | null,
  id: string,
): ContentLeaf | null {
  return leaves(node).find((l) => l.id === id) ?? null
}

/** The path to a leaf by id, or null. */
export function findLeaf(
  node: ContentNode | null,
  id: string,
  prefix: NodePath = [],
): NodePath | null {
  if (!node) return null
  if (node.type === 'leaf') return node.id === id ? prefix : null
  for (let i = 0; i < node.children.length; i++) {
    const hit = findLeaf(node.children[i], id, [...prefix, i])
    if (hit) return hit
  }
  return null
}

/** The node at a path, or null when the path does not resolve. */
export function nodeAt(
  node: ContentNode | null,
  path: NodePath,
): ContentNode | null {
  let current = node
  for (const index of path) {
    if (!current || current.type !== 'split') return null
    current = current.children[index] ?? null
  }
  return current
}

function replaceAt(
  node: ContentNode,
  path: NodePath,
  next: ContentNode | null,
): ContentNode | null {
  if (path.length === 0) return next
  if (node.type !== 'split') return node
  const [index, ...rest] = path
  const child = node.children[index]
  if (!child) return node
  const replaced = replaceAt(child, rest, next)
  const children = [...node.children]
  const sizes = [...node.sizes]
  if (replaced === null) {
    children.splice(index, 1)
    sizes.splice(index, 1)
  } else {
    children[index] = replaced
  }
  if (children.length === 0) return null
  return { type: 'split', direction: node.direction, children, sizes }
}

/** Close one pane. Null when it was the last one. */
export function closeLeaf(
  node: ContentNode | null,
  id: string,
): ContentNode | null {
  if (!node) return null
  const path = findLeaf(node, id)
  if (!path) return node
  if (path.length === 0) return null
  return normalize(replaceAt(node, path, null))
}

const DIRECTION_OF: Record<Exclude<DropEdge, 'center'>, Direction> = {
  left: 'row',
  right: 'row',
  top: 'column',
  bottom: 'column',
}

/**
 * Carry leaf IDS across a re-parse of the same tree.
 *
 * The url does not hold ids (they are internal addressing, meaningless to anyone
 * you send a link to), so every decode mints new ones. That is fine for the
 * value and fatal for the DOM: panes are keyed by leaf id, so a tree that came
 * back from the url with fresh ids unmounts and remounts EVERY pane — on every
 * change, including each pointer-move of a resize.
 *
 * Which is how dragging a seam broke. The first move rewrote the url, the tree
 * re-decoded, the pane remounted, and the seam the pointer had captured no
 * longer existed — so the drag stopped a few pixels in. The same remount threw
 * away scroll positions and reloaded every iframe, which is the opposite of what
 * keying by id was for.
 *
 * So: where the new tree is structurally the old one — same shape, same content
 * in the same place — the old ids are kept. Where it genuinely differs, the new
 * ids stand. Identity follows what a person would call "the same pane".
 */
export function adoptIds(
  prev: ContentNode | null,
  next: ContentNode,
): ContentNode {
  if (!prev) return next
  if (prev.type === 'leaf' && next.type === 'leaf') {
    return sameContent(prev.content, next.content)
      ? { ...next, id: prev.id }
      : next
  }
  if (
    prev.type === 'split' &&
    next.type === 'split' &&
    prev.direction === next.direction &&
    prev.children.length === next.children.length
  ) {
    return {
      ...next,
      children: next.children.map((child, i) =>
        adoptIds(prev.children[i] ?? null, child),
      ),
    }
  }
  return next
}

/**
 * Do two panes show the SAME thing? Per kind, since a session id and a document
 * id are different name spaces and an agent block is never a document.
 *
 * The PROJECT is part of the answer for an agent block, because two blocks on
 * `null` (the cross-project inbox) and on one project are showing different
 * lists. It is not part of it for a document: an id identifies the document
 * wherever it is catalogued, so the same document reached through two projects
 * is one pane, not two.
 */
export function sameContent(a: Content, b: Content): boolean {
  if (a.kind === 'agent' && b.kind === 'agent')
    return a.projectId === b.projectId && a.sessionId === b.sessionId
  // Two empty panes on the same project show the same list. Saying otherwise
  // would make `adoptIds` rename them on every change, which remounts them.
  if (a.kind === 'picker' && b.kind === 'picker')
    return a.projectId === b.projectId
  if (a.kind === 'discover' && b.kind === 'discover')
    return a.lineageId === b.lineageId
  if (a.kind === 'document' && b.kind === 'document') return a.docId === b.docId
  // BOTH HALVES, always. Two shells with the same id on different machines is
  // not a case that arises, but a block that matched on the shell alone could
  // reveal a pane pointed at another machine — and a terminal leaf naming its
  // device for its whole life is the one invariant this kind has.
  if (a.kind === 'terminal' && b.kind === 'terminal')
    return a.deviceId === b.deviceId && a.terminalId === b.terminalId
  return false
}

/**
 * The pane already showing this, or null.
 *
 * What "open it" should consult FIRST. Opening blind gives you a second pane on
 * the same document every time you click it in the sidebar — and since a new
 * pane lands beside whichever pane has focus, the copies pile up in the middle
 * of the layout rather than anywhere you would look for them.
 *
 * Two panes on one document remains expressible and is sometimes what you want
 * (the same file at two scroll positions); it just stops being what a single
 * click means. `openLeaf` still splits unconditionally, so a caller that means
 * "again" says so.
 */
export function leafShowing(
  node: ContentNode | null,
  content: Content,
): ContentLeaf | null {
  return leaves(node).find((l) => sameContent(l.content, content)) ?? null
}

/**
 * Open a pane beside (or in place of) the leaf `targetId`.
 *
 * `center` REPLACES what that pane shows, which is what clicking a document in
 * the sidebar means when a pane is focused: show it here, rather than splitting
 * again. Every other edge splits.
 *
 * `id` lets the caller NAME the pane it is about to create, which is how focus
 * can follow a split: the id is minted here, so a caller that did not supply one
 * has no way to say "and now put the caret in the pane I just made" short of
 * diffing the tree.
 */
export function openLeaf(
  node: ContentNode | null,
  content: Content,
  targetId: string | null,
  edge: DropEdge = 'center',
  id: string = leafId(),
): ContentNode {
  const fresh = leaf(content, id)
  if (!node) return fresh
  const path = targetId ? findLeaf(node, targetId) : null
  if (!path) {
    // No target: put it beside everything rather than replacing the root, so an
    // open from the sidebar with nothing focused never discards what is there.
    return normalize(split('row', [node, fresh])) ?? fresh
  }
  const target = nodeAt(node, path)
  if (!target) return node
  if (edge === 'center') {
    return normalize(replaceAt(node, path, fresh) ?? null) ?? fresh
  }
  const before = edge === 'left' || edge === 'top'
  const children = before ? [fresh, target] : [target, fresh]
  return (
    normalize(
      replaceAt(node, path, split(DIRECTION_OF[edge], children)) ?? null,
    ) ?? fresh
  )
}

/**
 * Change what ONE block shows, in place.
 *
 * This is the back button and the list's own navigation: an agent block moves
 * between the list and a conversation without becoming a different block, so its
 * position, size and focus are untouched. Distinct from `openLeaf`, which is for
 * bringing something new onto the screen.
 */
export function setLeafContent(
  node: ContentNode | null,
  id: string,
  content: Content,
): ContentNode | null {
  if (!node) return node
  const path = findLeaf(node, id)
  if (!path) return node
  const existing = nodeAt(node, path)
  if (!existing || existing.type !== 'leaf') return node
  return replaceAt(node, path, { ...existing, content }) ?? node
}

/** The files a document block holds, in order: its tabs, or the one it shows. */
export function paneTabs(content: Content): string[] {
  if (content.kind !== 'document') return []
  const tabs = content.tabs ?? []
  return tabs.includes(content.docId) ? tabs : [...tabs, content.docId]
}

/** A document block showing `docId` out of `tabs`, with `tabs` dropped at one. */
export function withTabs(
  content: DocumentContent,
  tabs: string[],
  docId: string,
): DocumentContent {
  const { tabs: _old, ...rest } = content
  return tabs.length > 1 ? { ...rest, docId, tabs } : { ...rest, docId }
}

const isCodePane = (l: ContentLeaf): boolean =>
  l.content.kind === 'document' && isCodeId(l.content.docId)

/**
 * Where a code file opens when a code pane is already in the space: as a tab
 * in that pane. Null when this is not a code file, or there is no code pane
 * to join, and the ordinary rules decide.
 *
 * A file that is already a tab somewhere is switched to, not added twice. The
 * pane joined is the focused one when that is a code pane, else the first,
 * the same predictability the document REUSE rule asks for. The new tab goes
 * last and is the one shown.
 *
 * Returns the tree and the pane it settled on, so a caller can focus it.
 */
export function openCodeTab(
  node: ContentNode | null,
  content: Content,
  focusedId: string | null,
): { tree: ContentNode; id: string } | null {
  if (!node || content.kind !== 'document' || !isCodeId(content.docId))
    return null
  // Only a pane of the same project: a code id names its source and path but
  // not its project, and a tab reads through its pane's project. A file from
  // another project in that pane asked the wrong project for its source.
  const project = content.projectId
  const all = leaves(node).filter(
    (l) => l.content.kind !== 'document' || l.content.projectId === project,
  )
  const holding = all.find((l) => paneTabs(l.content).includes(content.docId))
  if (holding) {
    const tree = selectTab(node, holding.id, content.docId) ?? node
    return { tree, id: holding.id }
  }
  const here = all.find((l) => l.id === focusedId)
  const target = here && isCodePane(here) ? here : all.find(isCodePane)
  if (!target || target.content.kind !== 'document') return null
  const tabs = [...paneTabs(target.content), content.docId]
  const tree =
    setLeafContent(
      node,
      target.id,
      withTabs(target.content, tabs, content.docId),
    ) ?? node
  return { tree, id: target.id }
}

/**
 * Open a code file as a tab in ONE named pane (RFC 0354): the pane's Files
 * column chose it, so it lands there and nowhere else. Switched to when the
 * pane already holds it; added last and shown when it does not. Unchanged when
 * the pane is not a code pane.
 */
export function addTab(
  node: ContentNode | null,
  id: string,
  docId: string,
): ContentNode | null {
  const target = leaves(node).find((l) => l.id === id)
  if (!target || !isCodePane(target) || target.content.kind !== 'document')
    return node
  if (!isCodeId(docId)) return node
  const tabs = paneTabs(target.content)
  if (tabs.includes(docId)) return selectTab(node, id, docId)
  return setLeafContent(
    node,
    id,
    withTabs(target.content, [...tabs, docId], docId),
  )
}

/** Show one of a pane's tabs. Unchanged when the pane does not hold it. */
export function selectTab(
  node: ContentNode | null,
  id: string,
  docId: string,
): ContentNode | null {
  const target = leaves(node).find((l) => l.id === id)
  if (!target || target.content.kind !== 'document') return node
  if (target.content.docId === docId) return node
  const tabs = paneTabs(target.content)
  if (!tabs.includes(docId)) return node
  return setLeafContent(node, id, withTabs(target.content, tabs, docId))
}

/**
 * Close one of a pane's tabs. Closing the one shown shows its neighbour, the
 * next one along, else the one before. Closing the LAST tab closes the pane,
 * which is null when that pane was the whole tree.
 */
export function closeTab(
  node: ContentNode | null,
  id: string,
  docId: string,
): ContentNode | null {
  const target = leaves(node).find((l) => l.id === id)
  if (!target || target.content.kind !== 'document') return node
  const tabs = paneTabs(target.content)
  const at = tabs.indexOf(docId)
  if (at === -1) return node
  const rest = tabs.filter((t) => t !== docId)
  if (rest.length === 0) return closeLeaf(node, id)
  const shown =
    target.content.docId === docId
      ? (rest[at] ?? rest[at - 1])
      : target.content.docId
  return setLeafContent(node, id, withTabs(target.content, rest, shown))
}

/**
 * Move an existing pane next to another. Remove-then-insert, which is what keeps
 * ids unique, with a centre drop meaning SWAP so two panes can trade places.
 */
export function moveLeaf(
  node: ContentNode | null,
  id: string,
  targetId: string,
  edge: DropEdge,
): ContentNode | null {
  if (!node || id === targetId) return node
  const from = findLeaf(node, id)
  const to = findLeaf(node, targetId)
  if (!from || !to) return node

  const moving = nodeAt(node, from)
  const target = nodeAt(node, to)
  if (!moving || moving.type !== 'leaf' || !target) return node

  if (edge === 'center' && target.type === 'leaf') {
    const swapped = replaceAt(node, to, moving)
    if (!swapped) return node
    return normalize(replaceAt(swapped, from, target) ?? null)
  }

  const without = closeLeaf(node, id)
  if (!without) return node
  const anchor = findLeaf(without, targetId)
  if (!anchor) return node
  const anchorNode = nodeAt(without, anchor)
  if (!anchorNode) return node
  const before = edge === 'left' || edge === 'top'
  const children = before ? [moving, anchorNode] : [anchorNode, moving]
  const direction = edge === 'center' ? 'row' : DIRECTION_OF[edge]
  return normalize(
    replaceAt(without, anchor, split(direction, children)) ?? null,
  )
}

/**
 * Resize: give `delta` (a fraction of the split's extent) to child `index` and
 * take it from the next, which is what dragging the seam between two panes means.
 * Clamped so neither falls below MIN_FRACTION.
 */
export function resizeSplit(
  node: ContentNode | null,
  path: NodePath,
  index: number,
  delta: number,
): ContentNode | null {
  if (!node) return node
  const target = nodeAt(node, path)
  if (!target || target.type !== 'split') return node
  const next = index + 1
  if (index < 0 || next >= target.children.length) return node
  const sizes = [...target.sizes]
  const a = sizes[index]
  const b = sizes[next]
  if (typeof a !== 'number' || typeof b !== 'number') return node
  const applied = Math.max(
    Math.min(delta, b - MIN_FRACTION),
    -(a - MIN_FRACTION),
  )
  if (Math.abs(applied) < 1e-9) return node
  sizes[index] = a + applied
  sizes[next] = b - applied
  return (
    replaceAt(node, path, {
      type: 'split',
      direction: target.direction,
      children: target.children,
      sizes,
    }) ?? node
  )
}

/**
 * Drop panes whose subject no longer exists — a closed session, a deleted
 * document. The tree is per-client and the subjects are shared, so it can outlive
 * them; a pane pointing at nothing renders as an error, which is worse than not
 * being there.
 */
export function pruneMissing(
  node: ContentNode | null,
  exists: (content: Content) => boolean,
): ContentNode | null {
  const strip = (n: ContentNode | null): ContentNode | null => {
    if (!n) return null
    if (n.type === 'leaf') return exists(n.content) ? n : null
    const kept = n.children
      .map((c, i) => [strip(c), n.sizes[i]] as const)
      .filter(([c]) => c !== null)
    return {
      type: 'split',
      direction: n.direction,
      children: kept.map(([c]) => c as ContentNode),
      sizes: kept.map(([, s]) => s ?? 0),
    }
  }
  return normalize(strip(node))
}

/**
 * How to arrange what is already open. A preset is a SHAPE, not a fixed tree:
 * leaves point at real sessions and documents, so a named arrangement cannot
 * carry its own content the way the old panel presets did.
 */
/** `single` keeps only the focused pane; the others lay every pane out. */
export type ArrangeShape = 'row' | 'column' | 'single'

export interface ArrangePreset {
  id: string
  name: string
  description: string
  shape: ArrangeShape
}

export const ARRANGE_PRESETS: ArrangePreset[] = [
  {
    id: 'columns',
    name: 'Columns',
    description: 'Everything side by side.',
    shape: 'row',
  },
  {
    id: 'stacked',
    name: 'Stacked',
    description: 'Everything in one column — for a wide, short window.',
    shape: 'column',
  },
  {
    id: 'focus',
    name: 'Focus',
    description: 'Just the pane you are in.',
    shape: 'single',
  },
]

/**
 * Apply a preset to the panes that are open.
 *
 * `single` CLOSES the others rather than hiding them, because content is
 * re-openable: a session is in the sidebar, a document is in the tree. Hiding
 * would mean a second kind of not-visible to reason about, which is what the
 * hidden-but-mounted panel state used to be.
 */
export function arrange(
  node: ContentNode | null,
  shape: ArrangeShape,
  focusedId: string | null,
): ContentNode | null {
  const all = leaves(node)
  if (all.length === 0) return null
  if (shape === 'single') {
    const keep = all.find((l) => l.id === focusedId) ?? all[0]
    return keep
  }
  if (all.length === 1) return all[0]
  return normalize(split(shape, all))
}

/** What to call the current arrangement, for a settings picker. */
export function arrangementName(node: ContentNode | null): string {
  const all = leaves(node)
  if (all.length <= 1) return 'Focus'
  if (node?.type === 'split' && node.children.every((c) => c.type === 'leaf')) {
    return node.direction === 'row' ? 'Columns' : 'Stacked'
  }
  return 'Custom'
}

/**
 * Validate an unknown blob as a content tree. Everything reaching the renderer
 * goes through here, because localStorage may hold a shape a previous version
 * wrote. Rule 1 is enforced by dropping repeat ids.
 */
export function parseContentTree(raw: unknown): ContentNode | null {
  const seen = new Set<string>()
  const walk = (value: unknown): ContentNode | null => {
    if (!value || typeof value !== 'object') return null
    const v = value as Record<string, unknown>
    if (v.type === 'leaf') {
      const c = v.content as Record<string, unknown> | undefined
      const id = typeof v.id === 'string' ? v.id : ''
      if (!id || seen.has(id) || !c) return null
      if (c.kind === 'picker') {
        seen.add(id)
        return {
          type: 'leaf',
          id,
          content: {
            kind: 'picker',
            projectId: typeof c.projectId === 'string' ? c.projectId : null,
          },
        }
      }
      if (c.kind === 'discover') {
        seen.add(id)
        return {
          type: 'leaf',
          id,
          content: {
            kind: 'discover',
            lineageId: typeof c.lineageId === 'string' ? c.lineageId : null,
          },
        }
      }
      if (c.kind === 'terminal' && typeof c.deviceId === 'string') {
        seen.add(id)
        return {
          type: 'leaf',
          id,
          content: {
            kind: 'terminal',
            deviceId: c.deviceId,
            terminalId: typeof c.terminalId === 'string' ? c.terminalId : null,
            ...(typeof c.projectId === 'string'
              ? { projectId: c.projectId }
              : {}),
          },
        }
      }
      if (c.kind === 'agent') {
        seen.add(id)
        // No project means the inbox, which cannot also be on a conversation:
        // a session is read through the project that catalogs it.
        const content: Content =
          typeof c.projectId === 'string'
            ? {
                kind: 'agent',
                projectId: c.projectId,
                sessionId: typeof c.sessionId === 'string' ? c.sessionId : null,
              }
            : { kind: 'agent', projectId: null, sessionId: null }
        return { type: 'leaf', id, content }
      }
      // A document block with no project cannot be rendered (nothing says which
      // catalog to read), so it is dropped rather than shown as an error.
      if (
        c.kind === 'document' &&
        typeof c.docId === 'string' &&
        typeof c.projectId === 'string'
      ) {
        seen.add(id)
        const tabs = Array.isArray(c.tabs)
          ? c.tabs.filter((t): t is string => typeof t === 'string')
          : []
        const content: DocumentContent = {
          kind: 'document',
          projectId: c.projectId,
          docId: c.docId,
        }
        return {
          type: 'leaf',
          id,
          content: withTabs(
            content,
            tabs.includes(c.docId) ? tabs : [],
            c.docId,
          ),
        }
      }
      return null
    }
    if (!Array.isArray(v.children)) return null
    const direction: Direction = v.direction === 'column' ? 'column' : 'row'
    const children = v.children
      .map(walk)
      .filter((c): c is ContentNode => c !== null)
    if (children.length === 0) return null
    const sizes = Array.isArray(v.sizes)
      ? (v.sizes as unknown[]).map((s) => (typeof s === 'number' ? s : 0))
      : []
    return normalize({
      type: 'split',
      direction,
      children,
      sizes:
        sizes.length === children.length ? sizes : equalSizes(children.length),
    })
  }
  const parsed = walk(raw)
  // Ids minted in a previous run must not collide with ones minted now.
  for (const l of leaves(parsed)) {
    const n = Number(l.id.replace(/^c/, ''))
    if (Number.isFinite(n) && n > seq) seq = n
  }
  return parsed
}
